import { useRef, useState } from 'react';
import { Paperclip, Upload, X, Loader2, ExternalLink } from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '../../lib/supabase';

const SIGNED_URL_TTL = 60 * 60 * 24 * 365;

// Trunca por chars com ellipsis no MEIO — preserva inicio + extensao.
// Visualmente bonito pra arquivos com nome gigante (ex: tokens de URL
// assinada do Drive ou Google Docs).
const truncateMiddle = (s, max = 40) => {
  const str = String(s || '');
  if (str.length <= max) return str;
  const keep = Math.floor((max - 1) / 2);
  return `${str.slice(0, keep)}…${str.slice(-keep)}`;
};

const filenameFromValue = (value) => {
  if (!value) return '';
  let raw = '';
  try {
    const u = new URL(value);
    const last = u.pathname.split('/').pop() || '';
    raw = decodeURIComponent(last).replace(/^\d+-/, '');
  } catch {
    raw = value.split('/').pop() || value;
  }
  return truncateMiddle(raw, 40);
};

export function FileInput({ value, onChange, onError, pathPrefix = 'solicitacoes', compress = false, onToast }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [compressStatus, setCompressStatus] = useState('');

  const handlePick = async (rawFile) => {
    if (!rawFile) return;
    let file = rawFile;
    // So aceita PDF. Validacao em DOIS criterios pra cobrir os casos em
    // que o browser nao seta o MIME corretamente (acontece em alguns
    // dispositivos/sistemas):
    //   1) Content-Type 'application/pdf' (caminho feliz)
    //   2) Extensao .pdf (case-insensitive) — fallback
    // Se nenhum bater, devolve erro pro user antes de tentar subir nada
    // pro Supabase Storage (poupa banda e dinheiro).
    const isPdfMime = file.type === 'application/pdf';
    const isPdfExt  = /\.pdf$/i.test(file.name || '');
    if (!isPdfMime && !isPdfExt) {
      // Reset do input pra deixar o user re-selecionar o mesmo arquivo
      // se quiser (browser nao dispara onChange pra mesma escolha).
      if (ref.current) ref.current.value = '';
      onError?.('Apenas arquivos PDF são aceitos. Converta o documento para PDF antes de anexar.');
      return;
    }
    // Compressao opcional (per-replica). Roda ANTES do upload pra economizar
    // storage e banda. Se PDF for pequeno ou ja otimizado, passa direto.
    if (compress) {
      try {
        setCompressStatus('Comprimindo…');
        const { compressPdf, fmtBytes } = await import('../../lib/pdf-compress');
        const result = await compressPdf(file, {
          onProgress: (p) => {
            if (p.stage === 'rendering') {
              setCompressStatus(`Comprimindo pág. ${p.page}/${p.total}…`);
            }
          },
        });
        setCompressStatus('');
        if (!result.skipped) {
          file = result.file;
          const pct = Math.round((1 - result.finalSize / result.originalSize) * 100);
          onToast?.(
            `Comprimido: ${fmtBytes(result.originalSize)} → ${fmtBytes(result.finalSize)} (-${pct}%)`,
            'ok'
          );
        }
      } catch (err) {
        setCompressStatus('');
        // Falha na compressao: segue com o arquivo original (nao bloqueia
        // o usuario). Log pra debug.
        console.warn('[FileInput] falha na compressao, seguindo sem:', err);
      }
    }
    setUploading(true);
    const safeName = file.name.replace(/[^\w.\-]+/g, '_');
    const path = `${pathPrefix}/${Date.now()}-${safeName}`;
    const { error: upErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(path, file, { contentType: file.type || 'application/pdf' });
    if (upErr) {
      setUploading(false);
      onError?.(`Falha no upload: ${upErr.message}`);
      return;
    }
    const { data: signed, error: signErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(path, SIGNED_URL_TTL);
    setUploading(false);
    if (signErr || !signed?.signedUrl) {
      onError?.(`Falha ao gerar URL: ${signErr?.message || 'erro desconhecido'}`);
      return;
    }
    onChange(signed.signedUrl);
  };

  const display = compressStatus
    ? compressStatus
    : uploading ? 'Enviando…'
    : (value ? filenameFromValue(value) : 'Clique para anexar arquivo');
  const isBusy = uploading || !!compressStatus;
  const Icon = isBusy ? Loader2 : (value ? Paperclip : Upload);

  return (
    // minWidth: 0 + overflow: hidden no wrapper garante que mesmo quando
    // o Field esta dentro de um grid track, o conteudo nao estoura.
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <input
        ref={ref}
        type="file"
        // Filtra o picker do SO pra mostrar so PDFs. Validacao real
        // (defensiva) acontece no handlePick acima — o accept aqui e
        // so UX (browser pode ignorar dependendo da plataforma).
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        onChange={e => handlePick(e.target.files?.[0])}
      />
      <button
        type="button"
        disabled={isBusy}
        onClick={() => ref.current?.click()}
        style={{
          width: '100%', height: 40, padding: '0 12px',
          background: 'var(--surface-2)',
          border: '1px dashed var(--border-2)',
          borderRadius: 'var(--radius)',
          display: 'flex', alignItems: 'center', gap: 10,
          color: value ? 'var(--ink)' : 'var(--muted)',
          fontSize: 13.5, cursor: uploading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          textAlign: 'left',
          // Mais defesa em profundidade: garante que o button respeite
          // a largura do pai (input file pode ter filename gigante).
          minWidth: 0, overflow: 'hidden',
        }}
      >
        <Icon size={14} className={isBusy ? 'spin' : ''} style={{ flexShrink: 0 }} />
        <span style={{
          flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </span>
        {value && !uploading && (
          <>
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: 'var(--muted-2)', display: 'inline-flex', padding: 2, flexShrink: 0 }}
              title="Abrir arquivo"
            ><ExternalLink size={14} /></a>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(''); } }}
              style={{ color: 'var(--muted-2)', padding: 2, cursor: 'pointer', flexShrink: 0 }}
              title="Remover"
            ><X size={14} /></span>
          </>
        )}
      </button>
      <style>{`
        @keyframes fileinput-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .spin { animation: fileinput-spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
